import React from 'react'
import ProductCard from '../components/ProductCard'
import type { Product } from '../components/ProductCard'
import { listProducts, type ApiProduct } from '../api/caApi'
import { useI18n } from '../state/I18nContext'
import DestinationSelector from '../components/DestinationSelector'
import { type Dest } from '../types/dest'

const Catalog: React.FC = () => {
  const { t, locale } = useI18n()
  const l = t.landing
  const [dest, setDest] = React.useState<Dest>('PT')
  const [products, setProducts] = React.useState<Product[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const pickCategoryLabel = (p: ApiProduct): string => {
    const main = p.categories.find(c => c.slug === 'bread' || c.slug === 'cakes' || c.slug === 'vegan')
    return main?.name || p.categories[0]?.name || t.catalog.fallbackCategory
  }

  const toUiProduct = (p: ApiProduct): Product => {
    const firstPhoto = p.photos.slice().sort((a, b) => a.sort - b.sort)[0]
    return {
      id: p.slug,
      name: p.name,
      price: p.price,
      image: firstPhoto?.url || '/apps/ca/img/decor/product_bread_03.jpg',
      badges: p.badges || [],
      category: pickCategoryLabel(p),
      description: p.description || '',
    }
  }

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const apiProducts = await listProducts({ lang: locale, destination: dest })
        if (!alive) return
        setProducts(apiProducts.map(toUiProduct))
      } catch (e: any) {
        if (!alive) return
        const code = String(e?.code || 'request_failed') as keyof typeof t.apiErrors
        const mapped = (t.apiErrors as any)[code] as string | undefined
        setError(mapped || t.catalog.loadError)
      } finally {
        if (!alive) return
        setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [locale, t, dest])

  const categories = Array.from(new Set(products.map(p => p.category)))
  return (
    <section id="catalog" className="max-w-6xl mx-auto px-4 py-12">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t.catalog.title}</h1>
          <p className="text-neutral-700 mt-2">{t.catalog.description}</p>
        </div>
        <div className="hidden"> // always hidden for future use
          {categories.map(cat => (
            <span key={cat} className="px-3 py-1 rounded-full border border-neutral-300 text-sm">{cat}</span>
          ))}
        </div>
      </div>
      <DestinationSelector
        dest={dest}
        onChange={setDest}
        banner={l.destSelector[dest].banner}
        labels={l.destSelector}
        className="mt-6"
        bannerClassName="mt-2 mb-2"
      />
      <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
        {loading && (
          <div className="sm:col-span-2 md:col-span-3 lg:col-span-3 text-neutral-700">{t.catalog.loading}</div>
        )}
        {error && (
          <div className="sm:col-span-2 md:col-span-3 lg:col-span-3 text-red-700">{error}</div>
        )}
        {products.map((p: Product) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  )
}

export default Catalog
